import { __decorate } from "tslib";
import React from 'react';
import classNames from 'classnames';
import Divider from '@material-ui/core/Divider';
import isEmail from 'isemail';
import { SSO_POPUP_NAME, SSO_POPUP_FEATURES } from '@/constants';
import * as ActionCreators from '@/store/actions/creators';
import * as Actions from '@/store/actions/types';
import * as Selectors from '@/store/selectors';
import Button from '@/components/controls/button';
import ErrorMessage from '@/components/error-message';
import GoogleSSOButton from '@/components/controls/google-sso-button';
import Link from '@/components/link';
import MicrosoftSSOButton from '@/components/controls/microsoft-sso-button';
import PixelsLogo from 'PixelsLogo';
import TextField from '@/components/controls/textfield';
import { withStyles } from '@/decorators';
import { connect } from '@/utils';
export const ENTER_KEY = 13;
export const styles = ({ palette, spacing, breakpoints }) => ({
    ccText: {
        paddingLeft: spacing(2),
        fontWeight: 700,
        fontSize: spacing(3.75),
    },
    divider: {
        marginTop: spacing(2),
    },
    errorMessage: {
        textAlign: 'center',
    },
    forgotPassword: {
        color: palette.primary.light,
        fontSize: spacing(2.5),
        textDecoration: 'none',
    },
    formContainer: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        [breakpoints.up('md')]: {
            marginTop: '5vh',
        },
    },
    formControl: {
        margin: 0,
    },
    formError: {
        position: 'static',
    },
    input: {
        border: '#DBDBDB 1px solid',
        borderRadius: 5,
        fontSize: spacing(3),
        height: spacing(9),
        width: '100%',
    },
    inputField: {
        padding: spacing(0, 2.5, 0, 2.5),
    },
    item: {
        [breakpoints.up('lg')]: {
            width: '25%',
            minWidth: '350px',
        },
        [breakpoints.down('sm')]: {
            width: '45%',
        },
        [breakpoints.down('xs')]: {
            width: '80%',
        },
        margin: `${spacing(1)}px 0`,
        minWidth: '320px',
        width: '33%',
    },
    loginButton: {
        backgroundColor: palette.primary.light,
        borderRadius: 5,
        fontSize: spacing(3),
        minHeight: spacing(7.5),
        minWidth: spacing(13.75),
        textTransform: 'none',
    },
    loginButtons: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
    },
    logo: {
        width: spacing(6),
        height: spacing(6),
        verticalAlign: spacing(-1.5),
    },
    signInText: {
        fontSize: spacing(6.25),
    },
});
export const actionCreators = {
    login: ActionCreators.authenticateUser,
    ssoLogin: ActionCreators.initiateSSOAuthentication,
    getExternalAuthProviders: ActionCreators.getExternalAuthenticationProviders,
    receiveMessageEvent: ActionCreators.receiveMessageEvent,
};
export const selectors = {
    externalAuthenticationProviders: Selectors.externalAuthenticationProvidersSelector,
};
let Login = class Login extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            isEmailValid: true,
            email: '',
            password: '',
        };
        this.submitForm = ({ keyCode }) => keyCode === ENTER_KEY && this.login();
        this.login = () => this.state.isEmailValid && this.props.login(this.state.email, this.state.password);
        this.validateEmail = () => {
            if (!isEmail.validate(this.state.email, { minDomainAtoms: 2 })) {
                this.setState({ isEmailValid: false });
            }
            else if (!this.state.isEmailValid) {
                this.setState({ isEmailValid: true });
            }
        };
        this.ssoLogin = (provider) => {
            const windowFeatures = Object.entries(SSO_POPUP_FEATURES[provider]).map(([key, value]) => `${key}=${value}`).join();
            this.ssoPopup = window.open('about:blank', SSO_POPUP_NAME, windowFeatures);
            this.props.ssoLogin(provider);
        };
        this.closeSSOPopup = () => {
            var _a;
            (_a = this.ssoPopup) === null || _a === void 0 ? void 0 : _a.close();
            this.ssoPopup = null;
        };
        this.receiveMessage = (event) => {
            if (event.origin !== window.location.origin)
                return;
            if (![Actions.AUTHENTICATE_SSO_USER, Actions.EXCHANGE_SSO_CODE_ERROR].includes(event.data.type))
                return;
            this.closeSSOPopup();
            this.props.receiveMessageEvent(event);
        };
        this.isMicrosoftAuthenicationProvider = () => { var _a; return (_a = this.props.externalAuthenticationProviders) === null || _a === void 0 ? void 0 : _a.map((provider) => provider.name.toLowerCase()).includes('azure'); };
        this.isGoogleAuthenicationProvider = () => { var _a; return (_a = this.props.externalAuthenticationProviders) === null || _a === void 0 ? void 0 : _a.map((provider) => provider.name.toLowerCase()).includes('google'); };
    }
    render() {
        const { classes } = this.props;
        const { isEmailValid } = this.state;
        return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <form id="login" onKeyDown={this.submitForm} className={classes.formContainer}>
        <div className={classes.item}>
          <PixelsLogo className={classes.logo}/>
          <span className={classes.ccText}>
            Command Center
          </span>
        </div>
        <div className={classNames(classes.item, classes.signInText)}>
          Sign in to continue
        </div>
        {this.isMicrosoftAuthenicationProvider() && (<div className={classes.item}>
            <MicrosoftSSOButton ssoLogin={() => this.ssoLogin('azure')}/>
          </div>)}
        {this.isGoogleAuthenicationProvider() && (<div className={classes.item}>
            <GoogleSSOButton ssoLogin={() => this.ssoLogin('google')}/>
          </div>)}
        {(this.isGoogleAuthenicationProvider() || this.isMicrosoftAuthenicationProvider()) && (<Divider classes={{ root: classes.divider }} className={classes.item}/>)}
        <div className={classes.item}>
          <TextField autoComplete="email" autoFocus disableUnderline={true} error={isEmailValid ? null : 'Please enter a valid email'} formControlClassName={classes.formControl} formErrorClassName={classes.formError} fullWidth id="email" inputClassName={classes.input} inputFieldClassName={classes.inputField} name="email" onBlur={this.validateEmail} onChange={(email) => this.setState({ email })} placeholder="Company@groupbycloud.com" value={this.state.email}/>
        </div>
        <div className={classes.item}>
          <TextField autoComplete="current-password" disableUnderline={true} formControlClassName={classes.formControl} fullWidth inputClassName={classes.input} inputFieldClassName={classes.inputField} name="password" onChange={(password) => this.setState({ password })} placeholder="Password" type="password" value={this.state.password}/>
        </div>
        <div>
        <ErrorMessage className={classes.errorMessage} forId="login"/>
        </div>
        <div className={classNames(classes.item, classes.loginButtons)} id="form-buttons">
          <Button variant="primary" onClick={this.login} buttonRootClassName={classes.loginButton} flat>
            Login
          </Button>
          <Link classes={{ root: classes.forgotPassword }} href="http://support.groupbyinc.com">
            Forgot Password?
          </Link>
        </div>
      </form>);
    }
    componentDidMount() {
        this.props.getExternalAuthProviders();
        window.addEventListener('message', this.receiveMessage);
        window.addEventListener('beforeunload', this.closeSSOPopup);
    }
    componentWillUnmount() {
        this.closeSSOPopup();
        window.removeEventListener('message', this.receiveMessage);
        window.removeEventListener('beforeunload', this.closeSSOPopup);
    }
};
Login = __decorate([
    (withStyles(styles)),
    connect(selectors, actionCreators)
], Login);
export default Login;
